"use client"

import type React from "react"
import { useState, useRef, useEffect, useCallback } from "react"
import { Play, Pause, Volume2, VolumeX, RotateCcw, SkipBack, SkipForward } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { cn } from "@/lib/utils"

interface AudioPlayerProps {
  src: string
  title?: string
  artist?: string
  className?: string
  autoPlay?: boolean
  showWaveform?: boolean
}

export function AudioPlayer({
  src,
  title,
  artist,
  className,
  autoPlay = false,
  showWaveform = true,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [canPlay, setCanPlay] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [waveformData, setWaveformData] = useState<number[]>([])
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        window.innerWidth < 768 ||
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      )
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Generate fallback waveform
  const generateFallbackWaveform = useCallback(() => {
    const samples = 80
    const data: number[] = []

    for (let i = 0; i < samples; i++) {
      // Create a more natural-looking waveform using sine waves with some randomness
      const baseWave = Math.sin((i / samples) * Math.PI * 4) * 0.5
      const noise = (Math.random() - 0.5) * 0.3
      const envelope = Math.sin((i / samples) * Math.PI) // Envelope to make it look more natural

      let value = (baseWave + noise) * envelope
      value = Math.abs(value) // Make it positive
      value = Math.max(0.1, Math.min(1, value)) // Ensure it's between 0.1 and 1

      data.push(value)
    }

    return data
  }, [])

  // Analyze audio for waveform (desktop only)
  const analyzeAudio = useCallback(async () => {
    if (!showWaveform || isMobile) return

    setIsAnalyzing(true)

    try {
      // Try to fetch and analyze the audio
      const response = await fetch(src, {
        mode: "cors",
        credentials: "omit",
      })

      if (!response.ok) {
        throw new Error("Failed to fetch audio")
      }

      const arrayBuffer = await response.arrayBuffer()

      // Check if Web Audio API is supported
      if (!window.AudioContext && !(window as any).webkitAudioContext) {
        throw new Error("Web Audio API not supported")
      }

      const AudioContext = window.AudioContext || (window as any).webkitAudioContext
      const audioContext = new AudioContext()

      // Handle suspended context (required for some browsers)
      if (audioContext.state === "suspended") {
        await audioContext.resume()
      }

      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
      const channelData = audioBuffer.getChannelData(0)

      // Sample the audio data
      const samples = 80
      const blockSize = Math.floor(channelData.length / samples)
      const waveform: number[] = []

      for (let i = 0; i < samples; i++) {
        const start = i * blockSize
        const end = start + blockSize
        let sum = 0

        for (let j = start; j < end && j < channelData.length; j++) {
          sum += Math.abs(channelData[j])
        }

        const average = sum / blockSize
        // Normalize and ensure minimum height
        const normalized = Math.max(0.1, Math.min(1, average * 2))
        waveform.push(normalized)
      }

      setWaveformData(waveform)
      audioContext.close()
    } catch (error) {
      console.warn("Audio analysis failed, using fallback waveform:", error)
      // Use fallback waveform
      setWaveformData(generateFallbackWaveform())
    } finally {
      setIsAnalyzing(false)
    }
  }, [src, showWaveform, isMobile, generateFallbackWaveform])

  // Draw waveform
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !waveformData.length || isMobile) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Set canvas size with device pixel ratio for crisp rendering
    const rect = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1

    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr

    ctx.scale(dpr, dpr)
    canvas.style.width = rect.width + "px"
    canvas.style.height = rect.height + "px"

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, rect.height)

    const barWidth = rect.width / waveformData.length
    const centerY = rect.height / 2
    const maxBarHeight = rect.height * 0.8

    waveformData.forEach((value, index) => {
      const barHeight = value * maxBarHeight
      const x = index * barWidth
      const y = centerY - barHeight / 2

      // Calculate progress - fix the highlighting logic
      const progress = duration > 0 ? currentTime / duration : 0
      const barProgress = (index + 1) / waveformData.length
      const isPlayed = barProgress <= progress

      // Set color based on progress - using brand purple
      ctx.fillStyle = isPlayed ? "#ba94c5" : "#374151" // Brand purple for played, gray for unplayed

      // Draw bar with rounded corners
      const radius = Math.min(barWidth * 0.3, 2)
      ctx.beginPath()
      ctx.roundRect(x + barWidth * 0.1, y, barWidth * 0.8, barHeight, radius)
      ctx.fill()
    })
  }, [waveformData, currentTime, duration, isMobile])

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleLoadedMetadata = () => {
      setDuration(audio.duration)
      setIsLoading(false)
      audio.volume = volume
    }

    const handleCanPlay = () => {
      setCanPlay(true)
      setError(null)
    }

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
    }

    const handlePlay = () => {
      setIsPlaying(true)
    }

    const handlePause = () => {
      setIsPlaying(false)
    }

    const handleVolumeChange = () => {
      setVolume(audio.volume)
      setIsMuted(audio.muted)
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
      audio.currentTime = 0
    }

    const handleError = () => {
      setError("Failed to load audio file")
      setIsLoading(false)
      setCanPlay(false)
    }

    const handleLoadStart = () => {
      setIsLoading(true)
      setError(null)
    }

    const handleDurationChange = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration)
      }
    }

    audio.addEventListener("loadedmetadata", handleLoadedMetadata)
    audio.addEventListener("durationchange", handleDurationChange)
    audio.addEventListener("canplay", handleCanPlay)
    audio.addEventListener("timeupdate", handleTimeUpdate)
    audio.addEventListener("play", handlePlay)
    audio.addEventListener("pause", handlePause)
    audio.addEventListener("volumechange", handleVolumeChange)
    audio.addEventListener("ended", handleEnded)
    audio.addEventListener("error", handleError)
    audio.addEventListener("loadstart", handleLoadStart)

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata)
      audio.removeEventListener("durationchange", handleDurationChange)
      audio.removeEventListener("canplay", handleCanPlay)
      audio.removeEventListener("timeupdate", handleTimeUpdate)
      audio.removeEventListener("play", handlePlay)
      audio.removeEventListener("pause", handlePause)
      audio.removeEventListener("volumechange", handleVolumeChange)
      audio.removeEventListener("ended", handleEnded)
      audio.removeEventListener("error", handleError)
      audio.removeEventListener("loadstart", handleLoadStart)
    }
  }, [volume])

  // Initialize waveform (desktop only)
  useEffect(() => {
    if (src && showWaveform && !isMobile) {
      analyzeAudio()
    }
  }, [src, showWaveform, isMobile, analyzeAudio])

  // Draw waveform when data changes (desktop only)
  useEffect(() => {
    if (!isMobile) {
      drawWaveform()
    }
  }, [drawWaveform, isMobile])

  // Handle window resize (desktop only)
  useEffect(() => {
    if (isMobile) return

    const handleResize = () => {
      drawWaveform()
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [drawWaveform, isMobile])

  const togglePlay = async () => {
    const audio = audioRef.current
    if (!audio || !canPlay) return

    try {
      if (isPlaying) {
        audio.pause()
      } else {
        await audio.play()
      }
    } catch (error) {
      console.error("Error playing audio:", error)
      setError("Unable to play audio. Please try again.")
    }
  }

  const toggleMute = () => {
    const audio = audioRef.current
    if (!audio) return

    audio.muted = !audio.muted
  }

  const updateVolume = (newVolume: number[]) => {
    const audio = audioRef.current
    if (!audio) return

    const volumeValue = newVolume[0]
    audio.volume = volumeValue

    if (volumeValue > 0 && audio.muted) {
      audio.muted = false
    }
    if (volumeValue === 0) {
      audio.muted = true
    }
  }

  const handleSeek = (newTime: number[]) => {
    const audio = audioRef.current
    if (!audio || !canPlay || !duration) return

    const seekTime = newTime[0]
    audio.currentTime = Math.max(0, Math.min(duration, seekTime))
  }

  const skip = (seconds: number) => {
    const audio = audioRef.current
    if (!audio || !canPlay) return

    const newTime = Math.max(0, Math.min(duration, audio.currentTime + seconds))
    audio.currentTime = newTime
  }

  const restart = () => {
    const audio = audioRef.current
    if (!audio) return

    audio.currentTime = 0
    if (!isPlaying && canPlay) {
      togglePlay()
    }
  }

  const formatTime = (time: number) => {
    if (!isFinite(time) || isNaN(time)) return "0:00"
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, "0")}`
  }

  const handleWaveformClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canPlay || !duration || isMobile) return

    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const progress = x / rect.width
    const newTime = progress * duration

    const audio = audioRef.current
    if (audio) {
      audio.currentTime = Math.max(0, Math.min(duration, newTime))
    }
  }

  if (error) {
    return (
      <div className={cn("bg-gray-900 rounded-lg p-4", className)}>
        <div className="text-center text-red-400">
          <div className="mb-2">⚠️</div>
          <p className="text-sm mb-3">{error}</p>
          <Button
            onClick={() => {
              setError(null)
              const audio = audioRef.current
              if (audio) {
                audio.load()
              }
            }}
            size="sm"
            variant="outline"
            className="border-gray-600 text-white hover:bg-gray-800"
          >
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("bg-white/5 rounded-lg p-4 space-y-4", className)}>
      <audio ref={audioRef} src={src} preload="metadata" crossOrigin="anonymous" playsInline />

      {/* Track info */}
      {(title || artist) && (
        <div className="text-center">
          {title && <h3 className="text-white font-medium text-sm md:text-base truncate">{title}</h3>}
          {artist && <p className="text-gray-400 text-xs md:text-sm truncate">{artist}</p>}
        </div>
      )}

      {/* Waveform (Desktop only) */}
      {showWaveform && !isMobile && (
        <div className="relative">
          {(isAnalyzing || isLoading) && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded">
              <div className="flex items-center gap-2 text-gray-400 text-xs md:text-sm">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#ba94c5]"></div>
                {isAnalyzing ? "Analyzing audio..." : "Loading..."}
              </div>
            </div>
          )}
          <canvas
            ref={canvasRef}
            className="w-full h-16 md:h-20 cursor-pointer rounded bg-black/50"
            onClick={handleWaveformClick}
          />
        </div>
      )}

      {/* Progress bar (Mobile or fallback) */}
      {(isMobile || !showWaveform) && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-white/60">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
          <div className="relative">
            <Slider
              value={[currentTime]}
              max={duration || 100}
              step={0.1}
              onValueChange={handleSeek}
              className="w-full [&_[role=slider]]:bg-[#ba94c5] [&_[role=slider]]:border-[#ba94c5] [&_.slider-range]:bg-[#ba94c5]"
              disabled={!canPlay || !duration}
            />
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-1 md:space-x-2">
          {/* Skip back */}
          <Button
            onClick={() => skip(-10)}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 p-1 md:p-2"
            disabled={!canPlay}
          >
            <SkipBack className="h-3 w-3 md:h-4 md:w-4" />
          </Button>

          {/* Play/Pause */}
          <Button
            onClick={togglePlay}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 p-1 md:p-2"
            disabled={!canPlay}
          >
            {isPlaying ? (
              <Pause className="h-4 w-4 md:h-5 md:w-5" />
            ) : (
              <Play className="h-4 w-4 md:h-5 md:w-5 ml-0.5" />
            )}
          </Button>

          {/* Skip forward */}
          <Button
            onClick={() => skip(10)}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 p-1 md:p-2"
            disabled={!canPlay}
          >
            <SkipForward className="h-3 w-3 md:h-4 md:w-4" />
          </Button>

          {/* Restart */}
          <Button
            onClick={restart}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20 p-1 md:p-2"
            disabled={!canPlay}
          >
            <RotateCcw className="h-3 w-3 md:h-4 md:w-4" />
          </Button>

          {/* Volume controls - desktop only */}
          <div className="hidden sm:flex items-center space-x-2 ml-2">
            <Button
              onClick={toggleMute}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 p-2"
              disabled={!canPlay}
            >
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </Button>
            <div className="w-16 md:w-20">
              <Slider
                value={[isMuted ? 0 : volume]}
                max={1}
                step={0.01}
                onValueChange={updateVolume}
                className="w-full [&_[role=slider]]:bg-[#ba94c5] [&_[role=slider]]:border-[#ba94c5] [&_.slider-range]:bg-[#ba94c5]"
                disabled={!canPlay}
              />
            </div>
          </div>

          {/* Mobile mute button */}
          <div className="sm:hidden">
            <Button
              onClick={toggleMute}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 p-1"
              disabled={!canPlay}
            >
              {isMuted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
            </Button>
          </div>
        </div>

        {/* Time display - only show on desktop when using waveform */}
        {!isMobile && showWaveform && (
          <div className="text-white text-xs md:text-sm font-mono">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        )}
      </div>
    </div>
  )
}
